import dayjs from "dayjs"

/**
 * JOAN-11527: admins and office managers can pick the mobile interface for
 * themselves while the new app is in early access. The choice, the nudge
 * dismissal and the switch date are stored only on the device — there is no
 * backend for them, so reinstalling the app resets everything.
 */
export const MOBILE_APP_INTERFACE_STORAGE_KEY = "mobile_app_interface"

export const MOBILE_APP_INTERFACES = {
  OLD: "old",
  NEW: "new",
} as const

export type MobileAppInterface =
  (typeof MOBILE_APP_INTERFACES)[keyof typeof MOBILE_APP_INTERFACES]

export const NEW_MOBILE_APP_FEEDBACK_REASONS = {
  MISSING_FEATURES: "missing_features",
  HARD_TO_NAVIGATE: "hard_to_navigate",
  TOO_SLOW: "too_slow",
  OLD_LOOK_BETTER: "old_look_better",
  SOMETHING_ELSE: "something_else",
} as const

export type NewMobileAppFeedbackReason =
  (typeof NEW_MOBILE_APP_FEEDBACK_REASONS)[keyof typeof NEW_MOBILE_APP_FEEDBACK_REASONS]

export const MOBILE_APP_INTERFACE_SWITCH_SOURCES = {
  POPUP: "popup",
  PROFILE: "profile",
} as const

export type MobileAppInterfaceSwitchSource =
  (typeof MOBILE_APP_INTERFACE_SWITCH_SOURCES)[keyof typeof MOBILE_APP_INTERFACE_SWITCH_SOURCES]

export const NEW_MOBILE_APP_NUDGE_DISMISS_METHODS = {
  NOT_NOW: "not_now",
  CLOSE: "close",
} as const

export type NewMobileAppNudgeDismissMethod =
  (typeof NEW_MOBILE_APP_NUDGE_DISMISS_METHODS)[keyof typeof NEW_MOBILE_APP_NUDGE_DISMISS_METHODS]

export const MOBILE_APP_INTERFACE_SOURCES = {
  USER_CHOICE: "user_choice",
  COMPANY_FLAG: "company_flag",
} as const

export type MobileAppInterfaceState = {
  selectedInterface: MobileAppInterface | null
  switchedToNewAt: string | null
  isNudgeDismissed: boolean
}

const DEFAULT_STATE: MobileAppInterfaceState = {
  selectedInterface: null,
  switchedToNewAt: null,
  isNudgeDismissed: false,
}

const isMobileAppInterface = (value: unknown): value is MobileAppInterface =>
  typeof value === "string" &&
  Object.values<string>(MOBILE_APP_INTERFACES).includes(value)

export const readMobileAppInterfaceState = (): MobileAppInterfaceState => {
  const stored = window.localStorage.getItem(MOBILE_APP_INTERFACE_STORAGE_KEY)

  if (!stored) {
    return DEFAULT_STATE
  }

  try {
    const parsed: unknown = JSON.parse(stored)

    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_STATE
    }

    const { selectedInterface, switchedToNewAt, isNudgeDismissed } =
      parsed as Record<keyof MobileAppInterfaceState, unknown>

    return {
      selectedInterface: isMobileAppInterface(selectedInterface)
        ? selectedInterface
        : null,
      switchedToNewAt:
        typeof switchedToNewAt === "string" ? switchedToNewAt : null,
      isNudgeDismissed: isNudgeDismissed === true,
    }
  } catch {
    return DEFAULT_STATE
  }
}

export const writeMobileAppInterfaceState = (
  state: MobileAppInterfaceState,
) => {
  window.localStorage.setItem(
    MOBILE_APP_INTERFACE_STORAGE_KEY,
    JSON.stringify(state),
  )
}

export const getDaysOnNewMobileAppInterface = (
  switchedToNewAt: string | null,
) => {
  if (!switchedToNewAt) {
    return 0
  }

  return Math.max(dayjs().diff(dayjs(switchedToNewAt), "day"), 0)
}
